import { browser } from '$app/env';
import { changeColor, getContrastYIQ, type InputStyleOptions } from '#lib/helper/color.js';
import { WidgetButtonStyle, type WidgetTheme } from '#lib/types/generated/index.js';
import { t } from '#lib/stores/i18n.js';
import type { TranslationVars } from '#lib/helper/locale.js';
import { get, writable } from 'svelte/store';
import { widgetConfig } from './core';

// Use this store to force dark mode for the widget, even in an app light theme
export const widgetColorsForceDarkMode = writable<boolean>(false);

export const updateWidgetTheme = (colorProfile: WidgetTheme): void => {
	changeColor(colorProfile.primary, 'widget-primary');
	changeColor(colorProfile.background, 'widget-background');

	if (colorProfile.pageBackgroundColor) {
		changeColor(colorProfile.pageBackgroundColor, 'widget-page-background');
	} else {
		changeColor('transparent', 'widget-page-background');
	}

	if (colorProfile.useDarkMode) {
		changeColor(colorProfile.dark.primary, 'widget-primary-dark');
		changeColor(colorProfile.dark.background, 'widget-background-dark');
		if (colorProfile.dark.pageBackgroundColor) {
			changeColor(colorProfile.dark.pageBackgroundColor, 'widget-page-background-dark');
		} else {
			changeColor('transparent', 'widget-page-background-dark');
		}
	} else {
		changeColor(colorProfile.primary, 'widget-primary-dark');
		changeColor(colorProfile.background, 'widget-background-dark');
		changeColor(getContrastYIQ(colorProfile.background), 'widget-background-dark-on');
		changeColor('transparent', 'widget-page-background-dark');
	}
};

export const getWidgetBackgroundContrastHexColor = (colorProfile: WidgetTheme): string => {
	return getContrastYIQ(
		colorProfile.useDarkMode &&
			browser &&
			window.matchMedia('(prefers-color-scheme: dark)').matches
			? colorProfile.dark.background
			: colorProfile.background
	);
};

export const classesDependingOnProfile = (lightClasses: string, darkClasses: string): string => {
	if (get(widgetConfig).theme.useDarkMode) {
		if (get(widgetColorsForceDarkMode)) {
			return darkClasses.replace(/dark:/g, '');
		}

		if (browser && window.matchMedia('(prefers-color-scheme: dark)').matches) {
			return darkClasses;
		}

		return lightClasses;
	} else {
		return lightClasses;
	}
};

export function getFontTitle(): string {
	const fontFamily = get(widgetConfig).theme.fontFamily;
	if (fontFamily === 'default') {
		return 'font-title font-extralight';
	}

	return `font-${fontFamily}-title`;
}

export function getRoundedClasses(
	themeButtonStyle: WidgetButtonStyle,
	rounded: WidgetButtonStyle | 'auto' = 'auto'
): string {
	let buttonStyle = themeButtonStyle;
	if (rounded !== 'auto') {
		buttonStyle = rounded;
	}

	switch (buttonStyle) {
		case WidgetButtonStyle.Sharp:
		case 'sharp':
			return 'rounded-none';
		case WidgetButtonStyle.Rounded:
		case 'rounded':
			return 'rounded-md';
		case WidgetButtonStyle.Circle:
		case 'circle':
			return 'rounded-full';
		default:
			return 'rounded-md';
	}
}

export const inputStyleOptions: InputStyleOptions = {
	bg: 'bg-widget-background dark:bg-widget-background-dark',
	textColor: 'text-widget-background-on dark:text-widget-background-dark-on',
	placeholder:
		' placeholder:text-widget-background-on/70 dark:placeholder:text-widget-background-dark-on/70',
	border: 'border border-widget-primary/70 dark:border-widget-primary-dark/70 focus:border-widget-primary dark:focus:border-widget-primary-dark',
	disabled:
		'disabled:bg-widget-background-on/5 dark:disabled:bg-widget-background-dark-on/5 disabled:border-widget-primary/30 dark:disabled:border-widget-primary-dark/30 disabled:text-widget-background-on/50 dark:disabled:text-widget-background-dark-on/50 disabled:placeholder:text-widget-background-on/30 dark:disabled:placeholder:text-widget-background-dark-on/30 disabled:cursor-not-allowed'
};

export function getWidgetTermsHtml(
	translateFn?: (key: string, values?: TranslationVars) => string
): string {
	const translate = translateFn || get(t);
	return translate(
		get(widgetConfig).isWhitelabel
			? 'widget-external.customer.termsAcceptation_whitelabel'
			: get(widgetConfig).termsLink || get(widgetConfig).termsContent
				? 'widget-external.customer.termsAcceptation'
				: 'widget-external.customer.termsAcceptation_reservatoo',
		{
			reservatoo_terms_link: '/terms-widget',
			business_terms_link: get(widgetConfig).termsLink
				? get(widgetConfig).termsLink!
				: `/widget/${get(widgetConfig).hash}/terms`
		}
	);
}
